// Sign-in through the auth-otp edge function (IMPLEMENTATION_PLAN §5): the server decides, rate-limits and logs;
// clients only send the identifier, then the code, and store the returned session. Shared by web and mobile.
import type { Whoami } from './types';

export type Channel = 'whatsapp' | 'email';

export interface Identifier {
  channel: Channel;
  phone?: string;
  email?: string;
  /** phone typed, code wanted by email ("send to my email instead") */
  fallback?: boolean;
}

export type AuthError = 'invalid' | 'rate_limited' | 'send_failed' | 'invalid_code' | 'unavailable';

export interface OtpSession {
  access_token: string;
  refresh_token: string;
}

/** Email if it looks like one, otherwise a phone number (normalised server-side). Web sign-in. */
export function parseIdentifier(raw: string): Identifier | null {
  const value = raw.trim();
  if (!value) return null;
  if (value.includes('@')) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) ? { channel: 'email', email: value } : null;
  }
  return parsePhone(value);
}

/**
 * Mobile-number format per country code (the server's sign-in allowlist is +60 / +65, app.otp_policy). Only the
 * *format* is checked here — an impossible number is refused on the first screen; whether a well-formed number is
 * registered is never revealed (the server answers every one the same way). New markets: add a row (and a country
 * picker in the UI); a number typed without a country code is Malaysian.
 */
const MOBILE_FORMAT: Record<string, RegExp> = {
  '+60': /^1\d{8,9}$/, // Malaysia: 01x-xxx xxxx / 011-xxxx xxxx → +60 1 + 8–9 digits (landlines 03-… are refused)
  '+65': /^[3689]\d{7}$/, // Singapore: 8 digits
};

/** "012-345 6789", "+60 12…", "60 12…", "0060 12…" → "+60123456789"; null when not a mobile number we accept. */
export function normalizeMobile(raw: string): string | null {
  const value = raw.trim();
  if (!/^[+\d\s().-]+$/.test(value)) return null;
  let v = value.replace(/[^\d+]/g, '');
  if (v.startsWith('00')) v = `+${v.slice(2)}`;
  if (!v.startsWith('+')) {
    if (v.startsWith('60')) v = `+${v}`;
    else if (v.startsWith('0')) v = `+60${v.slice(1)}`;
    else return null;
  }
  v = `+${v.slice(1).replace(/\+/g, '')}`;
  const code = Object.keys(MOBILE_FORMAT).find((c) => v.startsWith(c));
  return code && MOBILE_FORMAT[code]!.test(v.slice(code.length)) ? v : null;
}

/**
 * A mobile number only (sent as typed; the server normalises again). Mobile sign-in: the app never sends an email
 * address — the code goes by WhatsApp, or to the email on file through "send to my email instead"
 * (IMPLEMENTATION_PLAN §5).
 */
export function parsePhone(raw: string): Identifier | null {
  const value = raw.trim();
  return normalizeMobile(value) ? { channel: 'whatsapp', phone: value } : null;
}

function errorOf(status: number, data: Record<string, unknown>): AuthError {
  if (status === 429) return 'rate_limited';
  if (status === 400) return 'invalid';
  if (status === 401) return 'invalid_code';
  if (data.error === 'send_failed') return 'send_failed';
  return 'unavailable';
}

export interface OtpClient {
  requestCode(id: Identifier): Promise<AuthError | null>;
  verifyCode(id: Identifier, code: string): Promise<OtpSession | AuthError>;
}

export function otpClient(
  config: () => { supabaseUrl: string; supabaseAnonKey: string },
  fetchImpl: typeof fetch = (...a) => fetch(...a),
): OtpClient {
  const call = async (body: Record<string, unknown>) => {
    try {
      const { supabaseUrl, supabaseAnonKey } = config();
      const res = await fetchImpl(`${supabaseUrl}/functions/v1/auth-otp`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', apikey: supabaseAnonKey },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      return { status: res.status, data };
    } catch {
      return { status: 0, data: {} };
    }
  };
  return {
    async requestCode(id) {
      const { status, data } = await call({ action: 'request', ...id });
      return status === 200 ? null : errorOf(status, data);
    },
    async verifyCode(id, code) {
      const { status, data } = await call({ action: 'verify', ...id, code });
      if (status !== 200) return errorOf(status, data);
      const session = data.session as Partial<OtpSession> | undefined;
      return session?.access_token && session.refresh_token
        ? { access_token: session.access_token, refresh_token: session.refresh_token }
        : 'unavailable';
    },
  };
}

/** Why the app signed someone out on its own (shown once on the sign-in screen). */
export type SignOutReason = 'role_changed' | 'access_removed';

/**
 * How often an open app re-reads whoami to notice a role change or deactivation (owner, 2026-09-25: every 5 min).
 * Both apps also re-read it on focus / foreground and after a refused call, so this is only the ceiling.
 */
export const ACCESS_CHECK_MS = 5 * 60_000;

/**
 * Whether this person must be signed out now. The server already refuses them on the next request (the helpers
 * read the live membership); this only stops the app showing screens they no longer have. A super admin is never
 * signed out here — outside a visit they have no company by design, and a visit's role is theirs to switch.
 */
export function forcedSignOut(
  me: Pick<Whoami, 'platform_admin' | 'company' | 'support' | 'reauth'> | undefined,
): SignOutReason | null {
  if (!me || me.platform_admin || me.support) return null;
  if (!me.company) return 'access_removed';
  if (me.reauth) return 'role_changed';
  return null;
}
